import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRepository, useLogin } from '@city-sage/aurum-avis-labs-components';
import { useIsAuthenticated } from '@azure/msal-react';
import { toast } from 'sonner';
import { QueryKeys } from './QueryKeys';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface UseFollowProps {
  userId: string; // The user to potentially follow
}

export function useFollow({ userId }: UseFollowProps) {
  const { api } = useRepository();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [isFollowing, setIsFollowing] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const { promptLoginModal } = useLogin();

  // Get current user's account
  const { data: currentAccount } = useQuery({
    queryKey: QueryKeys.account.details(),
    queryFn: () => api.getMyAccount(),
    enabled: !!isAuthenticated,
  });

  // Check if current user is following the target user
  const { data: followRelationship, isLoading: isCheckingFollow } = useQuery({
    queryKey: QueryKeys.follow.isFollowing(currentAccount?.id || '', userId),
    queryFn: () =>
      api.findAllFollow({
        filter: JSON.stringify({
          followerId: currentAccount?.id,
          followingId: userId,
        }),
        // TODO this is hardcoded for now, we need to fix this
        limit: 1000,
      }),
    enabled: !!currentAccount?.id && !!userId && currentAccount?.id !== userId,
  });

  // Update follow status when data changes
  useEffect(() => {
    if (followRelationship) {
      setIsFollowing(followRelationship.length > 0);
    }
  }, [followRelationship]);

  // Follow/Unfollow mutation
  const followMutation = useMutation({
    mutationFn: async () => {
      if (isFollowing) {
        // Unfollow using the removeFollow API
        return api.removeFollow({ followingId: userId });
      } else {
        // Follow using the createFollow API
        return api.createFollow({
          createFollowRequest: { followingId: userId },
        });
      }
    },
    onSuccess: () => {
      setIsFollowing(!isFollowing);
      // Invalidate all follow-related queries
      queryClient.invalidateQueries({ queryKey: QueryKeys.follow.base });
      // Invalidate account queries in case they include follow counts
      queryClient.invalidateQueries({ queryKey: QueryKeys.account.base });

      const message = isFollowing
        ? t('pages.publicProfile.unfollowed')
        : t('pages.publicProfile.followed');
      toast.success(message);
    },
    onError: (error) => {
      console.error('Follow mutation error:', error);
      toast.error(t('pages.publicProfile.followError'));
    },
  });

  // Get follower count for the target user
  const { data: followers, isLoading: isLoadingFollowers } = useQuery({
    queryKey: QueryKeys.follow.followers(userId),
    queryFn: () =>
      api.findAllFollow({
        filter: JSON.stringify({ followingId: userId }),
        // TODO this is hardcoded for now, we need to fix this
        limit: 1000,
      }),
    enabled: !!userId,
  });

  // Get following count for the target user
  const { data: following, isLoading: isLoadingFollowing } = useQuery({
    queryKey: QueryKeys.follow.following(userId),
    queryFn: () =>
      api.findAllFollow({
        filter: JSON.stringify({ followerId: userId }),
        // TODO this is hardcoded for now, we need to fix this
        limit: 1000,
      }),
    enabled: !!userId,
  });

  const toggleFollow = () => {
    if (!isAuthenticated) {
      promptLoginModal({
        title: 'Sign in to follow',
        description:
          'Create an account or sign in to follow users and stay updated with their latest travel adventures.',
      });
      return;
    }

    if (!currentAccount?.id) {
      toast.error('You must be logged in to follow users');
      return;
    }

    if (currentAccount?.id === userId) {
      toast.error('You cannot follow yourself');
      return;
    }

    followMutation.mutate();
  };

  const isOwnProfile = currentAccount?.id === userId;
  const canFollow = !!currentAccount?.id && !isOwnProfile;

  return {
    // State
    isFollowing,
    isOwnProfile,
    canFollow,
    isLoading: isCheckingFollow || isLoadingFollowers || isLoadingFollowing,
    isPending: followMutation.isPending,

    // Counts
    followerCount: followers?.length || 0,
    followingCount: following?.length || 0,

    // Actions
    toggleFollow,

    // Data
    currentAccount,
    followers,
    following,
  };
}

// Hook for getting follow stats for any user (without follow/unfollow functionality)
export interface UseFollowStatsProps {
  userId: string;
}

export function useFollowStats({ userId }: UseFollowStatsProps) {
  const { api } = useRepository();

  // Get follower count
  const { data: followers, isLoading: isLoadingFollowers } = useQuery({
    queryKey: QueryKeys.follow.followers(userId),
    queryFn: () =>
      api.findAllFollow({
        filter: JSON.stringify({ followingId: userId }),
        // TODO this is hardcoded for now, we need to fix this
        limit: 1000,
      }),
    enabled: !!userId,
  });

  // Get following count
  const { data: following, isLoading: isLoadingFollowing } = useQuery({
    queryKey: QueryKeys.follow.following(userId),
    queryFn: () =>
      api.findAllFollow({
        filter: JSON.stringify({ followerId: userId }),
        // TODO this is hardcoded for now, we need to fix this
        limit: 1000,
      }),
    enabled: !!userId,
  });

  return {
    followerCount: followers?.length || 0,
    followingCount: following?.length || 0,
    isLoading: isLoadingFollowers || isLoadingFollowing,
    followers,
    following,
  };
}
